const EPSILON = 1e-10;

export interface ClockAdvanceResult {
  readonly steps: number;
  readonly droppedTime: number;
  readonly interpolationAlpha: number;
}

export class FixedStepClock {
  private accumulator = 0;
  private elapsed = 0;

  constructor(
    readonly stepSeconds = 1 / 60,
    readonly maximumStepsPerAdvance = 240,
  ) {
    if (!Number.isFinite(stepSeconds) || stepSeconds <= 0) {
      throw new Error("fixed step must be a positive finite duration");
    }
    if (
      !Number.isInteger(maximumStepsPerAdvance) ||
      maximumStepsPerAdvance < 1
    ) {
      throw new Error("maximum fixed steps must be a positive integer");
    }
  }

  get simulatedTime(): number {
    return this.elapsed;
  }

  get interpolationAlpha(): number {
    return this.accumulator / this.stepSeconds;
  }

  advance(
    realDeltaSeconds: number,
    step: (stepSeconds: number) => void,
  ): ClockAdvanceResult {
    if (!Number.isFinite(realDeltaSeconds) || realDeltaSeconds < 0) {
      throw new Error("frame delta must be a finite non-negative duration");
    }

    this.accumulator += realDeltaSeconds;
    let steps = 0;
    while (
      this.accumulator + EPSILON >= this.stepSeconds &&
      steps < this.maximumStepsPerAdvance
    ) {
      step(this.stepSeconds);
      this.accumulator -= this.stepSeconds;
      if (Math.abs(this.accumulator) < EPSILON) this.accumulator = 0;
      this.elapsed += this.stepSeconds;
      steps += 1;
    }

    let droppedTime = 0;
    if (
      steps === this.maximumStepsPerAdvance &&
      this.accumulator >= this.stepSeconds
    ) {
      const retained = this.accumulator % this.stepSeconds;
      droppedTime = this.accumulator - retained;
      this.accumulator = retained;
    }

    return { steps, droppedTime, interpolationAlpha: this.interpolationAlpha };
  }
}
